import React, { FC, ReactNode } from 'react';

import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { variant } from '@lucid/theme';
/* eslint-disable-next-line no-restricted-imports */
import { Link as GatsbyLink, GatsbyLinkProps } from 'gatsby';

import Icon from '../icon/icon';
import { LinkVariants, UnderlineType } from './link';

export interface LinkGatsbyProps extends GatsbyLinkProps<{}> {
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the link to `display="block"` for the link. */
  displayBlock?: boolean;
  /** Render an arrow on the right of the link. */
  hasArrow?: boolean;
  /** Determines if the link is active. */
  isActive?: boolean;
  /** Sets the color of the arrow icon. */
  iconColor?: string;
  /** Sets the color of the arrow icon onHover. */
  iconHoverColor?: string;
  /** Checks if the link should have inverse styles. */
  isInverse?: boolean;
  /** The link used in the Link component. */
  to: string;
  /** Sets the underline value for the Link. */
  underline?: UnderlineType;
  /** The available variants for the Link component. */
  variant?: LinkVariants;
}
// @ts-ignore
const StyledGatsbyLinkBase: FC<LinkGatsbyProps> = styled(
  GatsbyLink,
  {
    shouldForwardProp: isPropValid
  }
)(
  variant({
    scale: 'links',
    variants: {
      defaultVariant: {}
    }
  }),
  ({
    theme,
    underline,
    displayBlock,
    hasArrow,
    isInverse
  }) => ([
    {
      label: 'gatsbyLink',
      display: 'inline-block',
      fontWeight: theme?.fontWeights.bold,
      textDecoration: 'none',
      cursor: 'pointer'
    },
    isInverse && {
      color: `${theme.colors.white} !important`,
      '&:active, &:focus': {
        color: `${theme.colors.coolGrey[40]} !important`
      },
      '&:hover': {
        color: `${theme.colors.coolGrey[40]} !important`
      }
    },
    underline === 'always' && {
      textDecoration: 'underline'
    },
    underline === 'always' || underline === 'hover' && {
      '&:hover, &:active, &:focus': {
        textDecoration: 'underline'
      }
    },
    displayBlock && {
      display: 'block'
    },
    hasArrow && {
      '&:hover, &:focus': {
        '& > .hasArrow': {
          transform: 'translateX(0.15rem)'
        }
      }
    }
  ])
);

const StyledGatsbyLink = ({
  activeClassName,
  activeStyle,
  className,
  children,
  displayBlock,
  hasArrow,
  iconColor,
  iconHoverColor,
  onClick,
  partiallyActive,
  replace,
  state,
  to,
  ref,
  variant = 'primary',
  ...rest
}: LinkGatsbyProps) => (
  <StyledGatsbyLinkBase
    {...rest}
    activeClassName={activeClassName}
    /* eslint-disable-next-line react/jsx-sort-props */
    activeStyle={activeStyle}
    /* eslint-disable-next-line react/jsx-sort-props */
    className={className}
    data-link-as-gatsby="true"
    displayBlock={displayBlock}
    hasArrow={hasArrow}
    onClick={onClick}
    partiallyActive={partiallyActive}
    replace={replace}
    state={state}
    to={to}
    variant={variant}
  >
    {children}
    {hasArrow && (
      <span
        css={{
          label: 'hasArrow',
          position: 'relative',
          fontSize: '0.75em',
          marginLeft: '0.5em',
          display: 'inline-block',
          transition: 'transform 0.15s ease-out',
          color: 'inherit'
        }}
        /* eslint-disable-next-line react/jsx-sort-props */
        className="hasArrow"
      >
        <Icon color={iconColor} hoverColor={iconHoverColor} type="ArrowRight" />
      </span>
    )}
  </StyledGatsbyLinkBase>
);

export default StyledGatsbyLink;
